import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../../../hooks/useLanguage';
import { categories as frCategories } from '../../../../../locales/fr/categories';
import { categories as esCategories } from '../../../../../locales/es/categories';
import { categories as arCategories } from '../../../../../locales/ar/categories';
import useImageOptimizer from '../../../../../hooks/useImageOptimizer';
import useFormNavigation from '../../../../../hooks/useFormNavigation';

const translations = {
  fr: frCategories,
  es: esCategories,
  ar: arCategories
};

const CategoryForm = ({ category, isEditMode, categories, onSubmit, onCancel, isSubmitting, onResetToCreate }) => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const { optimizeImage, isOptimizing, error } = useImageOptimizer({
    maxInitialSize: 5,
    maxFinalSize: 1,
    maxWidth: 400,
    maxHeight: 400
  });
  const { handleFormKeyDown } = useFormNavigation();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: null
  });

  const [imagePreview, setImagePreview] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        image: category.image || null
      });
      setImagePreview(category.image || '');
    } else {
      // Réinitialiser les états quand il n'y a pas de catégorie
      setFormData({
        name: '',
        description: '',
        image: null
      });
      setImagePreview('');
    }
  }, [category]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t.form.validations.required;
    } else if (formData.name.length < 2) {
      newErrors.name = t.form.validations.minLength.replace('{min}', '2');
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = t.form.validations.maxLength.replace('{max}', '500');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const optimizedImage = await optimizeImage(file);
        if (optimizedImage) {
          setImagePreview(optimizedImage);
          setFormData(prev => ({ ...prev, image: optimizedImage }));
        }
      } catch (error) {
        console.error('Erreur lors de l\'optimisation de l\'image:', error);
        alert(error.message);
      }
    }
  };

  const handleRemoveImage = () => {
    setImagePreview('');
    setFormData(prev => ({ ...prev, image: null }));
  };

  return (
    <form 
      onSubmit={handleSubmit} 
      onKeyDown={handleFormKeyDown}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
        <div className="sm:col-span-3">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            {t.form.fields.name}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={t.form.placeholders.name}
            className={`input input-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 ${
              errors.name ? 'input-error' : ''
            }`}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
        <div className="sm:col-span-2">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            {t.form.fields.description}
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder={t.form.placeholders.description}
            rows={5}
            className={`textarea textarea-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 ${
              errors.description ? 'textarea-error' : ''
            }`}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description}</p>
          )}
        </div>

        <div>
          <label htmlFor="image" className="block text-sm font-medium text-gray-700">
            {t.form.fields.image}
          </label>
          <div className="mt-2">
            <input
              type="file"
              id="image"
              name="image"
              onChange={handleImageChange}
              accept="image/*"
              className="hidden"
            />
            <div className="relative inline-block">
              <div 
                className="relative w-32 h-32 rounded-lg overflow-hidden border-2 border-base-300 cursor-pointer hover:border-primary"
                onClick={() => document.getElementById('image').click()}
              >
                {imagePreview ? (
                  <img 
                    src={imagePreview} 
                    alt="Aperçu" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-base-200">
                    <svg className="w-12 h-12 text-base-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-xs text-base-300 mt-1">Cliquez pour ajouter une image</span>
                  </div>
                )}
                {imagePreview && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage();
                    }}
                    className="absolute top-1 right-1 btn btn-circle btn-xs btn-error"
                    title={t.form.buttons?.cancel || 'Supprimer'}
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center pt-4">
        <div className="flex space-x-2">
          {isEditMode && (
            <button
              type="button"
              onClick={() => {
                // Réinitialiser le formulaire pour une nouvelle catégorie
                setFormData({
                  name: '',
                  description: '',
                  image: null
                });
                setImagePreview('');
                setErrors({});
                
                // Passer en mode création
                if (onResetToCreate) {
                  onResetToCreate();
                }
              }}
              className="btn btn-secondary btn-sm"
              disabled={isSubmitting}
            >
              <span className="text-lg">+</span>
              {/* <span className="ml-1">Nouvelle catégorie</span> */}
            </button>
          )}
        </div>
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-ghost"
          >
            {t.form.cancel}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary"
          >
            {isSubmitting ? t.form.submitting : (isEditMode ? t.form.update : t.form.create)}
          </button>
        </div>
      </div>
    </form>
  );
};

export default CategoryForm; 